// Az ügyfél / fiók adatlap „Számlánként" nézete — a szolgáltatás-hozzárendelések a LEENDŐ SZÁMLÁK szerint.
//
// MIÉRT (user-kérés): az adatlapon a hozzárendelések lapos listája nem mondta meg, mi kerül az ügyfél közös
// számlájára, mi megy eltérítve egy fiókra, és milyen kibocsátóval / bizonylattal — vegyes ügyfélnél (pl. 3 hotel
// közösen, 2 eltérítve) ezt csak a generálás után lehetett látni. A csoportosítást a BFF a generátorral AZONOS
// szabállyal számolja (`invoice-generation/invoice-group.ts`), tehát a nézet nem tud mást ígérni, mint ami
// kiállításra kerül.
//
// A sorok NEVEIT a hozzárendelés-lista adja (ugyanaz a lekérés, mint az ügyfél-adatlap szekciójáé — a kulcs azonos,
// tehát nincs második letöltés), a nézet csak a csoportosítást kéri le. Összeget és esedékességet SZÁNDÉKOSAN nem
// mutat: ahhoz a teljes generálás kellene.
import {
  type ServiceAssignmentInvoiceGroupSchema,
  type ServiceAssignmentRowSchema,
} from '@atlas/contracts';
import { ExclamationTriangleIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';
import { type z } from 'zod';

import { InvoiceIssuerBadge } from '@/components/invoices/invoice-issuer-badge';
import { InvoiceTypeBadge } from '@/components/invoices/invoice-type-badge';
import { ServiceStatusBadge } from '@/components/service-assignments/service-status-badge';
import { DomainBadge } from '@/components/shared/domain-badge';
import { HAIRLINE_BOTTOM, HAIRLINE_ROW, HAIRLINE_TOP } from '@/components/shared/hairline';
import { RelatedList } from '@/components/shared/related-list';
import { Card } from '@/components/ui/card';
import { Segmented } from '@/components/ui/segmented';
import { Text } from '@/components/ui/text';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { isForbiddenStatus } from '@/lib/error-status';
import { assignmentSubLines } from '@/lib/service-plan-label';

type Row = z.infer<typeof ServiceAssignmentRowSchema>;
type Group = z.infer<typeof ServiceAssignmentInvoiceGroupSchema>;

/** Kinek a nézete. A fiók-nézet az ügyfél-szintű, a fiókot érintő hozzárendeléseket is mutatja. */
export type InvoiceGroupsTarget = { clientId: string } | { clientId: string; accountId: string };

/** A szolgáltatás-szekció nézete: a lapos lista (alapértelmezett) vagy a leendő számlák szerinti csoportosítás. */
export type AssignmentSectionView = 'list' | 'invoices';

/** A két adatlap (ügyfél · fiók) KÖZÖS nézet-váltója a szekció fejlécében. */
export function AssignmentViewToggle({
  value,
  onChange,
  t,
}: {
  value: AssignmentSectionView;
  onChange: (v: AssignmentSectionView) => void;
  t: TFunction;
}): JSX.Element {
  return (
    <Segmented
      aria-label={t('serviceAssignments.invoiceView.label')}
      value={value}
      onChange={(v) => onChange(v === 'invoices' ? 'invoices' : 'list')}
      options={[
        { value: 'list' as const, label: t('serviceAssignments.invoiceView.list') },
        { value: 'invoices' as const, label: t('serviceAssignments.invoiceView.invoices') },
      ]}
    />
  );
}

export const invoiceGroupsKey = (target: InvoiceGroupsTarget) =>
  [
    ...serviceAssignmentKeys.all,
    'invoice-groups',
    'accountId' in target ? `account:${target.accountId}` : `client:${target.clientId}`,
  ] as const;

function ItemRow({
  row,
  autoBillingOff,
  t,
}: {
  row: Row | undefined;
  autoBillingOff: boolean;
  t: TFunction;
}) {
  const lines = row ? assignmentSubLines(row, t) : [];
  return (
    <li className={cn('flex items-start justify-between gap-3 px-4 py-2.5', HAIRLINE_ROW)}>
      <div className="min-w-0">
        <Text variant="itemTitle" as="div" className="truncate">
          {row ? (row.serviceName ?? row.planName ?? '—') : '—'}
        </Text>
        {lines.map((line) => (
          <Text key={line} variant="secondary" as="div" className="truncate" title={line}>
            {line}
          </Text>
        ))}
        <Text variant="secondary" as="div" className="truncate">
          {row?.scope === 'client'
            ? t('serviceAssignments.invoiceView.clientScope')
            : (row?.accountName ?? '')}
        </Text>
        {autoBillingOff && (
          <Text variant="secondary" as="div" className="text-warning flex items-center gap-1">
            <ExclamationTriangleIcon className="size-3.5 shrink-0" aria-hidden />
            {t('serviceAssignments.invoiceView.autoBillingOff')}
          </Text>
        )}
      </div>
      {row && (
        <ServiceStatusBadge
          status={row.status}
          label={t(`serviceAssignments.statusLabel.${row.status}`, { defaultValue: row.status })}
        />
      )}
    </li>
  );
}

function GroupCard({ group, rows, t }: { group: Group; rows: Map<string, Row>; t: TFunction }) {
  const title =
    group.recipient === 'client'
      ? t('serviceAssignments.invoiceView.recipientClient', { name: group.recipientName ?? '—' })
      : t('serviceAssignments.invoiceView.recipientAccount', { name: group.recipientName ?? '—' });
  const autoOff = new Set(group.autoBillingOffIds);
  return (
    <Card data-testid="invoice-group">
      <div className={cn('space-y-1.5 px-4 py-3', HAIRLINE_BOTTOM)}>
        <Text variant="itemTitle" as="div">
          {title}
        </Text>
        {/* A FEJ-MEZŐK egy sorban, jelvényként (user-döntés): a bizonylat fajtája (e-számla / számla / díjbekérő) ·
            kibocsátó · fizetési megállapodás, mögöttük az ÁFA és a deviza — ezek bármelyikének eltérése külön számla. A profil-sor nincs:
            a címzett már a cím. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <InvoiceTypeBadge type={group.invoiceType} deliveryType={group.deliveryType} t={t} />
          <InvoiceIssuerBadge issuerKey={group.issuerKey} t={t} />
          <DomainBadge
            kind="agreement"
            value={group.agreement}
            label={t(`serviceAssignments.agreementLabel.${group.agreement}`, {
              defaultValue: group.agreement,
            })}
          />
          <Text variant="secondary" tone="tertiary">
            {t('serviceAssignments.invoiceView.vatCurrency', {
              vat: t(`invoices.vatRateLabel.${group.vatRate}`, { defaultValue: group.vatRate }),
              currency: group.currency,
            })}
          </Text>
        </div>
        {group.issuerMissing && (
          <Text variant="secondary" as="div" className="text-warning flex items-start gap-1">
            <ExclamationTriangleIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {t('serviceAssignments.invoiceView.issuerMissing')}
          </Text>
        )}
      </div>
      <ul>
        {group.assignmentIds.map((id) => (
          <ItemRow key={id} row={rows.get(id)} autoBillingOff={autoOff.has(id)} t={t} />
        ))}
      </ul>
      {group.otherCount > 0 && (
        <Text variant="secondary" as="div" className={cn('px-4 py-2', HAIRLINE_TOP)}>
          {t('serviceAssignments.invoiceView.otherCount', { count: group.otherCount })}
        </Text>
      )}
    </Card>
  );
}

export function AssignmentInvoiceGroups({
  target,
  t,
}: {
  target: InvoiceGroupsTarget;
  t: TFunction;
}): JSX.Element {
  const query = api.serviceAssignmentInvoiceGroups.useQuery(invoiceGroupsKey(target), {
    query: 'accountId' in target ? { accountId: target.accountId } : { clientId: target.clientId },
  });
  // a NEVEK az ügyfél hozzárendelés-listájából (az ügyfél-adatlap szekciójával azonos kulcs → nincs új letöltés)
  const list = api.serviceAssignments.useQuery(
    [...serviceAssignmentKeys.lists(), 'by-client', target.clientId],
    { query: { clientId: target.clientId, limit: 200 } },
  );
  const rows = new Map<string, Row>(
    (list.data?.status === 200 ? list.data.body.assignments : []).map((r) => [r.id, r]),
  );

  if (query.isLoading || query.isError || query.data?.status !== 200) {
    return (
      <RelatedList
        items={[]}
        isLoading={query.isLoading}
        isError={query.isError}
        forbidden={isForbiddenStatus(query.error?.status)}
        onRetry={() => void query.refetch()}
        showAllLabel={() => ''}
        emptyLabel={t('serviceAssignments.invoiceView.empty')}
        emptyIcon={Square3Stack3DIcon}
        errorLabel={t('serviceAssignments.invoiceView.error')}
        forbiddenLabel={t('serviceAssignments.invoiceView.forbidden')}
        retryLabel={t('serviceAssignments.invoiceView.retry')}
      />
    );
  }
  const { groups, excluded } = query.data.body;

  return (
    <div className="space-y-3" data-testid="invoice-groups">
      <Text variant="secondary" tone="tertiary" as="p">
        {t('serviceAssignments.invoiceView.note')}
      </Text>
      {groups.length === 0 && (
        <Text variant="secondary" as="p">
          {t('serviceAssignments.invoiceView.empty')}
        </Text>
      )}
      {groups.map((g) => (
        <GroupCard key={g.key} group={g} rows={rows} t={t} />
      ))}
      {excluded.length > 0 && (
        <Card data-testid="invoice-group-excluded">
          <div className={cn('px-4 py-3', HAIRLINE_BOTTOM)}>
            <Text variant="itemTitle" as="div">
              {t('serviceAssignments.invoiceView.excludedTitle')}
            </Text>
          </div>
          <ul>
            {excluded.map((e) => {
              const row = rows.get(e.assignmentId);
              return (
                <li
                  key={e.assignmentId}
                  className={cn('flex items-start justify-between gap-3 px-4 py-2.5', HAIRLINE_ROW)}
                >
                  <div className="min-w-0">
                    <Text variant="itemTitle" as="div" className="truncate">
                      {row ? (row.serviceName ?? row.planName ?? '—') : '—'}
                    </Text>
                    <Text variant="secondary" as="div">
                      {t(`serviceAssignments.invoiceView.reason.${e.reason}`)}
                    </Text>
                  </div>
                  {row && (
                    <ServiceStatusBadge
                      status={row.status}
                      label={t(`serviceAssignments.statusLabel.${row.status}`, {
                        defaultValue: row.status,
                      })}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
